import { useEffect, useState } from 'react';
import { Icon } from './Icon';
import { cn } from './kit';

/**
 * RETOUR EN HAUT — un seul geste, et il ne s'impose jamais.
 *
 * Trois décisions, dans l'ordre où elles se posent :
 *
 *   1. **Il n'existe pas tant qu'on n'a pas défilé.** Sous 720 px de course, le
 *      bouton n'est ni visible ni cliquable ni annoncé (`aria-hidden`, hors du
 *      parcours de tabulation) : la page haute de deux écrans n'a pas besoin
 *      d'un ascenseur, et un bouton flottant au repos est un meuble.
 *   2. **Il ne se démonte pas.** Il reste dans le document et change d'état par
 *      une classe : une transition est possible, et surtout le focus ne tombe
 *      jamais dans le vide quand il s'efface — un bouton qui disparaît pendant
 *      qu'il a le focus renvoie le clavier au début de la page.
 *   3. **Il connaît ce qui flotte déjà.** Sur téléphone, la fiche produit porte
 *      une barre d'achat collante en bas : le bouton se pose au-dessus d'elle
 *      (5,5 rem) au lieu de la chevaucher. Sur grand écran, il descend à 1,75 rem.
 *
 * Le défilement est celui du document (`scroll-behavior: smooth` est déjà posé
 * sur `html`). En `prefers-reduced-motion`, la feuille annule ce lissage : on
 * n'ajoute donc **pas** de `behavior: 'smooth'` en JavaScript, qui le
 * rétablirait contre la préférence du lecteur.
 */
export function RetourEnHaut() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let frame = 0;
    const mesurer = () => {
      frame = 0;
      setVisible(window.scrollY > 720);
    };
    /* Un seul calcul par image, même si le navigateur émet quarante événements
       de défilement par seconde : `requestAnimationFrame` les fond en un. */
    const planifier = () => {
      if (!frame) frame = requestAnimationFrame(mesurer);
    };
    mesurer();
    window.addEventListener('scroll', planifier, { passive: true });
    window.addEventListener('resize', planifier);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', planifier);
      window.removeEventListener('resize', planifier);
    };
  }, []);

  const remonter = () => {
    window.scrollTo({ top: 0 });
    /* Le focus revient au début du contenu : sans cela, il reste sur un bouton
       qui vient de s'effacer, et la tabulation suivante repart du haut du
       document sans que rien ne l'annonce. */
    const cible = document.getElementById('contenu');
    if (cible) {
      cible.setAttribute('tabindex', '-1');
      cible.focus({ preventScroll: true });
    }
  };

  return (
    <button
      type="button"
      onClick={remonter}
      className={cn('retour-haut', visible && 'retour-haut--visible')}
      aria-label="Revenir en haut de la page"
      title="Revenir en haut"
      aria-hidden={!visible}
      tabIndex={visible ? 0 : -1}
    >
      <Icon name="arrowUp" size={20} />
    </button>
  );
}
